/**
 * WHAT WE SPEND to run one AI call and to keep one recording — deliberately separate from
 * creditsModel.ts (what the CUSTOMER pays) so the two can never be read as the same number.
 *
 * Two kinds of figure live here, and the Spend screen labels them differently on purpose:
 *
 *   METERED  — model spend. Real token counts read off every Gemini response's `usageMetadata`,
 *              priced at the published per-million rates below. Measured, not invoiced.
 *   MODELLED — recording storage. The byte size and the retention window are exact; the ₹/GiB is
 *              a list price, so the row is a good estimate of a line on a future Azure bill.
 *
 * Azure infrastructure (Functions, Container Apps, Postgres, ACS) is deliberately ABSENT. It is a
 * fixed monthly bill nothing in the request path can attribute to a candidate, and inventing a
 * per-unit share of it is exactly how the old ₹15/scan and ₹130/interview constants ended up
 * wrong by two and three orders of magnitude.
 */
import { FX_TO_INR } from "./creditsModel.js";
/**
 * Per-model list prices, keyed by the EXACT model id we put on the wire (including the `-latest`
 * aliases we actually run) so a lookup never depends on normalising a version string.
 */
export const MODEL_PRICES = {
    "gemini-flash-lite-latest": { inputPerMTokUsd: 0.05, outputPerMTokUsd: 0.2 },
    "gemini-2.5-flash-lite": { inputPerMTokUsd: 0.05, outputPerMTokUsd: 0.2 },
    "gemini-2.5-flash": { inputPerMTokUsd: 0.3, outputPerMTokUsd: 2.5 },
    // Anthropic judge (env-gated, off by default — ANTHROPIC_JUDGE_MODEL). First-party API list
    // prices. Listed here so the per-call AI usage log (ai_call_log) prices a Claude call at Claude
    // rates; cost_ledger's Gemini-only rollup never looks these up.
    "claude-sonnet-5": { inputPerMTokUsd: 2, outputPerMTokUsd: 10 },
    "claude-sonnet-4-6": { inputPerMTokUsd: 3, outputPerMTokUsd: 15 },
    "claude-opus-5": { inputPerMTokUsd: 5, outputPerMTokUsd: 25 },
    "claude-haiku-4-5": { inputPerMTokUsd: 1, outputPerMTokUsd: 5 },
};
/**
 * Every call site defaults to flash-lite, so an UNLISTED model is priced as one. That under-reports
 * the day someone points GEMINI_JUDGE_MODEL at a bigger model without adding it above — add the
 * row in the same commit as the model switch.
 */
export const DEFAULT_MODEL_PRICE = MODEL_PRICES["gemini-flash-lite-latest"];
/**
 * THE CENTRAL PRICE BOOK OVERRIDES (ai_models, migration 0092). MODEL_PRICES above is the code
 * default and the seed; once a process has loaded the admin price book (aiModelConfig.ts), a model
 * priced there is priced from there — for ai_call_log AND for the cost_ledger rollup, so an owner
 * editing a rate can never leave two different numbers for the same model. Seeded with exactly the
 * values above, so installing it changes nothing until someone edits a rate.
 */
const tokenPriceOverrides = new Map();
export function installTokenPriceOverrides(entries) {
    tokenPriceOverrides.clear();
    for (const e of entries) {
        tokenPriceOverrides.set(e.model, {
            price: { inputPerMTokUsd: e.inputPerMTokUsd, outputPerMTokUsd: e.outputPerMTokUsd },
            ref: e.ref,
        });
    }
}
/** The price-book row that prices `model`, when the admin price book has one. */
export function tokenPriceRef(model) {
    return tokenPriceOverrides.get(model)?.ref ?? null;
}
export function modelPriceFor(model) {
    const listed = tokenPriceOverrides.get(model)?.price ?? MODEL_PRICES[model];
    if (listed)
        return listed;
    // An unlisted Claude model must never be priced at the Gemini default (~40x under-report).
    return providerOfModel(model) === "anthropic" ? DEFAULT_ANTHROPIC_MODEL_PRICE : DEFAULT_MODEL_PRICE;
}
/** Which provider serves a model id — the model id is the only reliable signal (aliases, fallbacks). */
export function providerOfModel(model) {
    const m = (model ?? "").toLowerCase();
    if (m === "fake-judge")
        return "fake";
    if (m.startsWith("gemini"))
        return "gemini";
    if (m.startsWith("claude"))
        return "anthropic";
    return m || "unknown";
}
/** True when `model` has its own price — false means it is being priced at a fallback rate. */
export function isListedModel(model) {
    return tokenPriceOverrides.has(model) || Object.prototype.hasOwnProperty.call(MODEL_PRICES, model);
}
/**
 * Fallback for an UNLISTED Claude model. Pricing one at the flash-lite default would under-report
 * it ~40×; Sonnet is the tier the judge defaults to. The per-call log flags the row as unlisted.
 */
export const DEFAULT_ANTHROPIC_MODEL_PRICE = MODEL_PRICES["claude-sonnet-5"];
/**
 * FX is the owner-console REPORTING rate from creditsModel.ts — the same rate revenue rolls up on,
 * so cost and revenue land on one axis. It is not a settlement rate: Google bills us in USD and
 * nothing here converts money for a customer.
 */
const USD_TO_INR = FX_TO_INR.USD;
/** Round to the ledger's precision (numeric(14,4)) in one place, so nothing drifts by a rounding. */
function toLedger(inr) {
    return Math.round(inr * 1e4) / 1e4;
}
/**
 * ₹ cost of ONE model call. Four decimals because a single flash-lite call costs a fraction of a
 * paisa — at 2dp most calls would round to zero and the whole meter would read as free.
 */
export function modelCostInr(model, inTokens, outTokens) {
    const price = modelPriceFor(model);
    const usd = (Math.max(0, inTokens) / 1e6) * price.inputPerMTokUsd +
        (Math.max(0, outTokens) / 1e6) * price.outputPerMTokUsd;
    return toLedger(usd * USD_TO_INR);
}
/**
 * Read `usageMetadata` off a Gemini generateContent response. Returns null when the field is
 * absent (an error body, a stub, an older API surface) so the caller records NOTHING rather than
 * a confident zero.
 *
 * `thoughtsTokenCount` is the trap. flash-lite runs with dynamic thinking on — we cannot pin
 * thinkingBudget:0, it 400s — those tokens bill at the output rate, and they are NOT part of
 * `candidatesTokenCount`. Omit them and the judge's ~700 thinking tokens per grade vanish from the
 * ledger. `totalTokenCount − promptTokenCount` is the fallback when the breakdown is missing.
 */
export function geminiUsage(model, body) {
    const meta = body?.usageMetadata;
    if (!meta)
        return null;
    const num = (v) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0);
    const inputTokens = num(meta.promptTokenCount);
    const counted = num(meta.candidatesTokenCount) + num(meta.thoughtsTokenCount);
    const outputTokens = counted > 0 ? counted : Math.max(0, num(meta.totalTokenCount) - inputTokens);
    if (inputTokens === 0 && outputTokens === 0)
        return null;
    return { model, provider: "gemini", inputTokens, outputTokens };
}
/**
 * Read `usage` off an Anthropic Messages response. Null when absent, so nothing is recorded rather
 * than a confident zero. Cache reads/creations bill at the input rate here (a judge call sends no
 * cache markers, so they are normally 0).
 */
export function anthropicModelUsage(model, body) {
    const u = body?.usage;
    if (!u)
        return null;
    const num = (v) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0);
    const inputTokens = num(u.input_tokens) + num(u.cache_creation_input_tokens) + num(u.cache_read_input_tokens);
    const outputTokens = num(u.output_tokens);
    if (inputTokens === 0 && outputTokens === 0)
        return null;
    return { model, provider: "anthropic", inputTokens, outputTokens };
}
/** Spend of a batch of calls split by the provider that served each — never one label for a mixed run. */
export function usageCostByProvider(usage) {
    const by = new Map();
    for (const u of usage) {
        const p = u.provider ?? providerOfModel(u.model);
        (by.get(p) ?? by.set(p, []).get(p)).push(u);
    }
    return [...by].map(([provider, list]) => ({ provider, costInr: usageCostInr(list), calls: list.length }));
}
/** Total ₹ of a batch of calls — how a multi-call unit (one scan, one whole interview) is priced. */
export function usageCostInr(usage) {
    return toLedger(usage.reduce((sum, u) => sum + modelCostInr(u.model, u.inputTokens, u.outputTokens), 0));
}
/** Token totals for a batch — what the log line carries so a ₹ figure can be re-priced later. */
export function usageTokens(usage) {
    return usage.reduce((acc, u) => ({ calls: acc.calls + 1, in: acc.in + u.inputTokens, out: acc.out + u.outputTokens }), { calls: 0, in: 0, out: 0 });
}
/**
 * Azure Blob hot/LRS list price in Central India, USD per GiB-month. MODELLED, not invoiced: the
 * recording's byte size and its retention window are both exact, so the rate is the only estimate
 * in the number. Update when the Azure price list moves.
 */
export const BLOB_STORAGE_USD_PER_GIB_MONTH = 0.021;
/**
 * Playback egress, USD per GiB. NOT metered anywhere — nothing observes a recruiter pressing play —
 * so the ledger UNDER-reports video cost by about this much per view (≈₹1.15 for a 150 MB
 * recording). The Spend screen states the omission instead of letting it read as a zero.
 */
export const BLOB_EGRESS_USD_PER_GIB = 0.0868;
const BYTES_PER_GIB = 1024 ** 3;
/** ₹ cost of holding `bytes` for `days` at the list rate above. */
export function storageCostInr(bytes, days) {
    const gib = Math.max(0, bytes) / BYTES_PER_GIB;
    const months = Math.max(0, days) / 30;
    return toLedger(gib * months * BLOB_STORAGE_USD_PER_GIB_MONTH * USD_TO_INR);
}
/** ₹ egress of ONE playback of a `bytes`-sized recording. Quoted in the UI; never ledgered. */
export function egressCostInr(bytes) {
    return toLedger((Math.max(0, bytes) / BYTES_PER_GIB) * BLOB_EGRESS_USD_PER_GIB * USD_TO_INR);
}
//# sourceMappingURL=modelCost.js.map